import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildGame } from '../api/gameBuilderAPI';
import MissionGame from './MissionGame';
import MissionTransition from './MissionTransition';
import SiteMap, { roomState } from './SiteMap';
import { playableChain } from './chain';
import { clockOf, layoutSite, pathBetween, pointAlong, spotOf } from './siteLayout';
import './SitePlayer.css';

/**
 * Plays a plan on its site map (backend plan.save_site). The map sets the stage: the player sees
 * every room and what happens there, then clicks a glowing room to go - the "you" marker walks the
 * corridor, the plan zooms into the room and its mission begins. After a win:
 *   - the same room and a time link in the storyline: the clock transition (MissionTransition) runs
 *     straight into the next mission - same place, later;
 *   - otherwise back to the map, the mission ticked off and where to go next glowing.
 * With a storyline, its order is followed (only the next mission's room glows); without one, any
 * room with a built game can be played, in any order. Every game is already built: no AI calls.
 */
const API_BASE = process.env.REACT_APP_API_URL || '';
const asset = (src) => (src && src.startsWith('/game-builder-assets') ? `${API_BASE}${src}` : src);
const WALK_MS_PER_UNIT = 2.4;
const ZOOM_MS = 950;
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function SitePlayer({ plan, onClose, page = false }) {   // page: in its own window (GamePages.jsx)
  const site = plan.site;
  const layout = useMemo(() => layoutSite(site.rooms, site.theme), [site.rooms, site.theme]);
  const roomOf = useMemo(() => Object.fromEntries(site.rooms.flatMap((r) => r.missions.map((m) => [m, r.id]))), [site.rooms]);
  const names = Object.fromEntries(plan.missions.map((m) => [m.id, m]));
  const ready = plan.missions.filter((m) => plan.status?.[m.id]?.game).map((m) => m.id);
  const story = playableChain(plan);          // the game chain's painted games, in order (one being rebuilt is skipped)
  const playable = story ? story.missions : site.rooms.flatMap((r) => r.missions).filter((id) => ready.includes(id));

  const [specs, setSpecs] = useState(null);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('intro');      // intro | map | walking | zoom | mission | clock | complete
  const [done, setDone] = useState([]);
  const [where, setWhere] = useState('entrance');
  const [marker, setMarker] = useState(null);
  const [selected, setSelected] = useState(null);
  const [current, setCurrent] = useState(null);    // mission being played
  const [note, setNote] = useState('');
  const raf = useRef(null);
  const timer = useRef(null);
  useEffect(() => () => { cancelAnimationFrame(raf.current); clearTimeout(timer.current); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = {};
        for (const id of playable) {
          out[id] = await buildGame(plan.id, id);          // built games: quick, no AI calls
          if (cancelled) return;
        }
        setSpecs(out);
        setMarker(spotOf(layout, 'entrance'));
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [plan.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const nextInStory = story ? story.missions.find((id) => !done.includes(id)) : null;
  const statusOf = (id) => {
    if (!playable.includes(id)) return 'unready';
    if (done.includes(id)) return 'done';
    if (story) return id === nextInStory ? 'next' : 'locked';
    return 'open';
  };
  const missions = Object.fromEntries(plan.missions.map((m) => [m.id, { name: m.name, status: statusOf(m.id) }]));
  const upcoming = current && !done.includes(current) ? current : (story ? nextInStory : null);
  const clock = clockOf(specs?.[upcoming || current]?.intro);

  const go = (missionId) => {
    const target = roomOf[missionId];
    setSelected(null);
    setNote('');
    setCurrent(missionId);
    const enter = () => {
      setWhere(target);
      setPhase('zoom');
      timer.current = setTimeout(() => setPhase('mission'), reducedMotion() ? 200 : ZOOM_MS);
    };
    if (where === target) { enter(); return; }
    const path = pathBetween(layout, where, target);
    const length = path.slice(1).reduce((s, p, i) => s + Math.hypot(p.x - path[i].x, p.y - path[i].y), 0);
    const duration = reducedMotion() ? 300 : Math.min(2800, 500 + length * WALK_MS_PER_UNIT);
    const start = performance.now();
    setPhase('walking');
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
      setMarker(pointAlong(path, eased));
      if (t < 1) raf.current = requestAnimationFrame(step);
      else enter();
    };
    raf.current = requestAnimationFrame(step);
  };

  const backToMap = () => {
    setPhase('map');
    setMarker(spotOf(layout, where));
  };

  const won = () => {
    const nowDone = [...done, current];
    setDone(nowDone);
    if (playable.every((id) => nowDone.includes(id))) { setMarker(spotOf(layout, where)); setPhase('complete'); return; }
    if (story) {
      const i = story.missions.indexOf(current);
      const next = story.missions[i + 1];
      const link = story.links[i];
      if (link && link.type === 'time' && roomOf[next] === roomOf[current]) { setPhase('clock'); return; }
      setNote(link ? `${link.type === 'place' ? '🚶' : '🕐'} ${link.label || names[next]?.name}` : '');
    }
    backToMap();
  };

  const clickRoom = (roomId) => {
    const room = site.rooms.find((r) => r.id === roomId);
    if (roomState(room, missions) !== 'go') {
      setNote(room.missions.every((id) => statusOf(id) === 'done') ? `Everything in ${room.name} is done.` : `Nothing to do in ${room.name} yet.`);
      setSelected(null);
      return;
    }
    setSelected(roomId);
  };

  const restart = () => { setDone([]); setWhere('entrance'); setMarker(spotOf(layout, 'entrance')); setCurrent(null); setNote(''); setPhase('map'); };

  if (error || !specs) {
    return createPortal(
      <div className={`spl-backdrop ${page ? 'is-page' : ''}`} role="dialog" aria-modal="true">
        <div className="spl-card">
          <h3>{error ? "The map couldn't open" : `Opening ${site.name}…`}</h3>
          {error ? <p>{error}</p> : <p><span className="spl-spinner" /> Getting {playable.length} mission{playable.length === 1 ? '' : 's'} ready</p>}
          <button type="button" className="spl-btn spl-btn-quiet" onClick={onClose}>Close</button>
        </div>
      </div>,
      document.body
    );
  }

  if (phase === 'mission' || phase === 'clock') {
    const i = story ? story.missions.indexOf(current) : -1;
    const next = story ? story.missions[i + 1] : null;
    const sameRoomLater = story && next && story.links[i]?.type === 'time' && roomOf[next] === roomOf[current];
    return (
      <>
        <MissionGame
          page={page}
          key={current}
          spec={specs[current]}
          chapter={story ? `${i + 1} / ${story.missions.length}` : `${done.length + 1} / ${playable.length}`}
          onClose={backToMap}
          winLabel={playable.filter((id) => id !== current).every((id) => done.includes(id)) ? 'finish' : sameRoomLater ? story.links[i].label || 'later' : 'back to the map'}
          onWin={won}
        />
        {phase === 'clock' && (
          <MissionTransition link={story.links[i]} fromSrc={specs[current].background_src} toSrc={specs[next].background_src}
            nextName={names[next]?.name} onDone={() => { setCurrent(next); setPhase('mission'); }} />
        )}
      </>
    );
  }

  const selRoom = selected && site.rooms.find((r) => r.id === selected);
  return createPortal(
    <div className={`spl-backdrop ${page ? 'is-page' : ''} spl-map`} role="dialog" aria-modal="true" aria-label={site.name}>
      <header className="spl-bar">
        <div>
          <h3>{site.name}</h3>
          <span className="spl-sub">{done.length} of {playable.length} mission{playable.length === 1 ? '' : 's'} done{story ? ' · following the storyline' : ''}</span>
        </div>
        {clock && <span className="spl-clock" aria-label={`Time ${clock}`}>🕐 {clock}</span>}
        <button type="button" className="spl-close" onClick={onClose} aria-label="Leave the map">×</button>
      </header>

      <div className={`spl-stage ${phase === 'zoom' ? 'is-zooming' : ''}`}>
        <div className="spl-scroll">
          <SiteMap layout={layout} missions={missions} marker={marker} selected={selected}
            zoom={phase === 'zoom' ? where : null} title={site.name} onRoom={phase === 'map' ? clickRoom : null} />
        </div>
        {phase === 'zoom' && specs[current] && <div className="spl-zoom-scene" style={{ backgroundImage: `url(${asset(specs[current].background_src)})` }} />}

        {note && phase === 'map' && <div className="spl-note" role="status">{note}</div>}

        {selRoom && phase === 'map' && (
          <aside className="spl-room" aria-label={selRoom.name}>
            <button type="button" className="spl-close spl-close-dark" onClick={() => setSelected(null)} aria-label="Close">×</button>
            <h4>{selRoom.name}</h4>
            {selRoom.missions.filter((id) => statusOf(id) !== 'unready').map((id) => {
              const st = statusOf(id);
              return (
                <div key={id} className={`spl-mission is-${st}`}>
                  <strong>{id} · {names[id].name}</strong>
                  <p>{names[id].objective}</p>
                  {(st === 'next' || st === 'open') && <button type="button" className="spl-btn" onClick={() => go(id)}>Go{where === selRoom.id ? '' : ` to ${selRoom.name}`} ›</button>}
                  {st === 'done' && <span className="spl-tag is-done">✓ Done</span>}
                  {st === 'locked' && <span className="spl-tag">🔒 Later in the story</span>}
                </div>
              );
            })}
          </aside>
        )}

        {phase === 'intro' && (
          <div className="spl-overlay">
            <div className="spl-card">
              <h3>{site.name}</h3>
              {site.intro && <p>{site.intro}</p>}
              <p className="spl-muted">{playable.length} mission{playable.length === 1 ? '' : 's'} across {site.rooms.filter((r) => r.missions.some((id) => playable.includes(id))).length} room{site.rooms.length === 1 ? '' : 's'}.
                {story ? ' Follow the story: the room to go to next glows.' : ' Glowing rooms have a mission waiting - take them in any order.'}</p>
              <p className="spl-muted">Click a room to see what happens there, then go.</p>
              <button type="button" className="spl-btn" onClick={() => setPhase('map')}>Look at the map ›</button>
            </div>
          </div>
        )}

        {phase === 'complete' && (
          <div className="spl-overlay">
            <div className="spl-card">
              <h3>All missions complete</h3>
              <ol className="spl-list">{playable.map((id) => <li key={id}>✓ {names[id].name} <span className="spl-muted">· {site.rooms.find((r) => r.id === roomOf[id])?.name}</span></li>)}</ol>
              <div className="spl-actions">
                <button type="button" className="spl-btn" onClick={restart}>Play again</button>
                <button type="button" className="spl-btn spl-btn-quiet" onClick={onClose}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
